import React from "react";
import "./list_item_supply_slip.css";
import { Table, Input, Button, Space, Tabs } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import useSWR from "swr";
import { FaCheck } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
interface LisItemSupplySlipProps {
    workOrderNo?: string;
    existingItemCodes?: string[];
    onOk: (selectedRows: any[]) => void;
    onCancel: () => void;
}


const LisItemSupplySlip: React.FC<LisItemSupplySlipProps> = ({ workOrderNo, existingItemCodes = [], onOk, onCancel }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const OKbtn = FaCheck as React.ComponentType<any>;
    const Cancelbtn = MdOutlineCancel as React.ComponentType<any>;
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
    const [searchText, setSearchText] = React.useState('');
    const [searchedColumn, setSearchedColumn] = React.useState('');
    const { data: dataSource, isLoading } = useSWR(
        workOrderNo ? `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items/${workOrderNo}` : null,
        fetcher
    );

    const { data: woData } = useSWR(
        workOrderNo ? `${process.env.REACT_APP_BACKEND_URL}/api/work-order/items/${workOrderNo}` : null,
        fetcher
    );

    const processedData = React.useMemo(() => {
        if (!dataSource || !woData) {
            return (dataSource || []).map((ssItem: any) => ({
                ...ssItem,
                type: ssItem.type || '1'
            }));
        }
        return dataSource.map((ssItem: any) => {
            const woItem = woData.find((w: any) => w.item_code === ssItem.item_code);
            const qtyWO = woItem ? Number(woItem.supply_qty || 0) : 0;
            return {
                ...ssItem,
                qty: qtyWO,
                type: ssItem.type || '1',
                wo_type: woItem ? String(woItem.type) : undefined
            };
        });
    }, [dataSource, woData]);
    const getColumnSearchProps = (dataIndex: string): any => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value: any, record: any) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    });

    const columns: ColumnsType<any> = [
        { title: 'Item Code', dataIndex: 'item_code', key: 'item_code', sorter: (a, b) => a.item_code.localeCompare(b.item_code), ...getColumnSearchProps('item_code') },
        { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', sorter: (a, b) => a.item_name.localeCompare(b.item_name), ...getColumnSearchProps('item_name') },
        { title: 'Location Rack', dataIndex: 'location', key: 'location', sorter: (a, b) => a.location.localeCompare(b.location), ...getColumnSearchProps('location') },
        { title: 'Qty', dataIndex: 'qty', key: 'qty', sorter: (a, b) => a.qty - b.qty },
    ];

    const handleOk = () => {
        const selectedSpareparts = (processedData || []).filter((item: any) => selectedRowKeys.includes(item.id));
        onOk([...selectedSpareparts]);
        setSelectedRowKeys([]);
    };
    const handleCancel = () => {
        setSelectedRowKeys([]);
        onCancel();
    };

    return (
        <div id="i3b9" style={transformStyle}>
            <div className="gjs-grid-row" id="i2fu">
                <div className="gjs-grid-column" id="i208a">
                    <Tabs
                        defaultActiveKey="1"
                        items={[
                            {
                                key: '1',
                                label: 'Spareparts List',
                                children: (
                                    <Table
                                        rowKey="id"
                                        rowSelection={{
                                            selectedRowKeys,
                                            onChange: (keys) => setSelectedRowKeys(keys),
                                            getCheckboxProps: (record) => ({
                                                disabled: existingItemCodes.includes(record.item_code) || (record.wo_type === '1' && record.item_code !== 'External Part' && record.qty < 1),
                                                name: record.item_code,
                                            }),
                                        }}
                                        rowClassName={(record) => (existingItemCodes.includes(record.item_code) || (record.wo_type === '1' && record.item_code !== 'External Part' && record.qty < 0)) ? 'row-disabled' : ''}
                                        columns={columns}
                                        dataSource={processedData}
                                        loading={isLoading || (!woData && !!workOrderNo)}
                                        pagination={{ pageSize: 10 }}
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
            <Button
                className="gr-button"
                onClick={handleOk}
            >
                <OKbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                OK
            </Button>
            <Button
                className="gr-button"
                onClick={handleCancel}
            >
                <Cancelbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                Cancel
            </Button>
        </div>
    );
};

export default LisItemSupplySlip;
